import { DailyValueCalculationMethod } from "@/models/Recipes/DailyValueCalculationMethod";
import { Nutritions } from "@/models/Recipes/Nutritions";

export function debounce(callback: any, time = 250) {

  let intervalId: any = localStorage.getItem("some-unique-key-interval-id");

  if (intervalId) {
    clearTimeout(intervalId);
  }

  intervalId = setTimeout(() => {
    callback();
  }, time);

  localStorage.setItem("some-unique-key-interval-id", intervalId);
}

export function getBoldString(mainString: string, subString: string) {
  if(subString == ""){
    return  `<b>${mainString}</b>`;
  }
  else{
    const index = mainString.toLowerCase().indexOf(subString.toLowerCase());
    if (index !== -1) {
      const startIndex = mainString.substring(0, index);
      const endIndex = mainString.substring(index + subString.length);
      const highlightedString = startIndex + "<b>" + mainString.substring(index, index + subString.length) + "</b>" + endIndex;
      return highlightedString;
    }
    return mainString;
  }
}


export function round(number: number, decimalPlaces: number) {
  return Math.round(number * Math.pow(10, decimalPlaces)) / Math.pow(10, decimalPlaces);
}

export function shorten(text: string) {

  let newText = text.replace(',', '');
  newText = newText.replace('.', '');

  let parts = newText.split(' ');
  parts = parts.map((part) => {
    if (part.length > 3)
      return part.substr(0, 3) + '.';
    else {
      return part;
    }
  });

  return parts.join(' ');
}

export function toCamelCase(text: string) {

  let parts = text.split(' ');
  for (let i = 0; i < parts.length; i++) {

    parts[i] = parts[i].charAt(0).toUpperCase() + parts[i].slice(1);
  }

  let cammeled = parts.join('');
  cammeled = cammeled.replace(',', '');

  return cammeled;
}

export function desanitizeString(html: string) {
  let txt = document.createElement("textarea");
  txt.innerHTML = html;
  return txt.value;
}

export function getErrorMessage(error: any, translations: any) {
  return error.response &&
        error.response.data.errorMessages[0].userVisibleMessage
    ? error.response.data.errorMessages[0].userVisibleMessage
    : translations.get("toastr_network_error");
}

// Fallowing functions are used to simplify the object nutritionAnalysis returned from backend

export function mapNutritions(nutritions: any, dailyValueCalculationMethod?: DailyValueCalculationMethod): Partial<Nutritions> {
  const dvCheck = (roundedDv: number, exactDv: number): number => {
    return dailyValueCalculationMethod ? exactDv : roundedDv;
  };

  const roundingCheck = (value: any) => {
    if (dailyValueCalculationMethod == undefined) {
      return value.roundedValue;
    }

    else {
      if (dailyValueCalculationMethod == DailyValueCalculationMethod.Unrounded) {
        return Number.isInteger(value.exactValue) ? value.exactValue: value.exactValue.toFixed(2);
      }
      else if(dailyValueCalculationMethod == DailyValueCalculationMethod.Rounded) {
        return Number.isInteger(value.roundedValue) ? value.roundedValue: value.roundedValue.toFixed(2);
      }
      else {
        return value.roundedValue;
      }
    }
  };

  return {
    addedSugarPerServing: nutritions.addedSugarPerServing.exactValue,
    unadjustedAddedSugarPerServing: nutritions.unadjustedAddedSugarPerServing.exactValue,
    sugarAlcohols: roundingCheck(nutritions.alcoholSugarPerServing),
    alcoholSugarDv: dvCheck(
      nutritions.alcoholSugarPerServing.roundedDailyValue,
      nutritions.alcoholSugarPerServing.exactDailyValue
    ),
    iron: roundingCheck(nutritions.ironPerServing),
    ironDv: dvCheck(
      nutritions.ironPerServing.roundedDailyValue,
      nutritions.ironPerServing.exactDailyValue
    ),
    calciumDv: dvCheck(
      nutritions.calciumPerServing.roundedDailyValue,
      nutritions.calciumPerServing.exactDailyValue
    ),
    calcium: roundingCheck(nutritions.calciumPerServing),
    potassiumDv: dvCheck(
      nutritions.potassiumPerServing.roundedDailyValue,
      nutritions.potassiumPerServing.exactDailyValue
    ),
    potassium: roundingCheck(nutritions.potassiumPerServing),
    vitaminDDv: dvCheck(
      nutritions.vitaminDPerServing.roundedDailyValue,
      nutritions.vitaminDPerServing.exactDailyValue
    ),
    vitaminD: roundingCheck(nutritions.vitaminDPerServing),
    vitaminC: roundingCheck(nutritions.vitaminCPerServing),
    sodiumDv: dvCheck(
      nutritions.sodiumPerServing.roundedDailyValue,
      nutritions.sodiumPerServing.exactDailyValue
    ),
    sodium: roundingCheck(nutritions.sodiumPerServing),
    cholesterol: roundingCheck(nutritions.cholesterolPerServing),
    cholesterolDv: dvCheck(
      nutritions.cholesterolPerServing.roundedDailyValue,
      nutritions.cholesterolPerServing.exactDailyValue
    ),
    protein: roundingCheck(nutritions.proteinPerServing),
    sugarsDv: dvCheck(
      nutritions.sugarPerServing.roundedDailyValue,
      nutritions.sugarPerServing.exactDailyValue
    ),
    sugars: roundingCheck(nutritions.sugarPerServing),
    fibre: roundingCheck(nutritions.fibrePerServing),
    fibreDv: dvCheck(
      nutritions.fibrePerServing.roundedDailyValue,
      nutritions.fibrePerServing.exactDailyValue
    ),
    carbohydrate: roundingCheck(nutritions.carbohydratePerServing),
    carbohydrateDv: dvCheck(
      nutritions.carbohydratePerServing.roundedDailyValue,
      nutritions.carbohydratePerServing.exactDailyValue
    ),
    transFatDv: dvCheck(
      nutritions.transFatPerServing.roundedDailyValue,
      nutritions.transFatPerServing.exactDailyValue
    ),
    transFat: roundingCheck(nutritions.transFatPerServing),
    saturatedFatDv: dvCheck(
      nutritions.saturatedFatPerServing.roundedDailyValue,
      nutritions.saturatedFatPerServing.exactDailyValue
    ),
    saturatedFat: roundingCheck(nutritions.saturatedFatPerServing),
    fatDv: dvCheck(
      nutritions.fatPerServing.roundedDailyValue,
      nutritions.fatPerServing.exactDailyValue
    ),
    fat: roundingCheck(nutritions.fatPerServing),
    calories: roundingCheck(nutritions.energyPerServing),
    servingWeight: nutritions.weightPerServing,
    saturatedAndTransDv: dvCheck(
      nutritions.saturatedAndTransFatPerServing.roundedDailyValue,
      nutritions.saturatedAndTransFatPerServing.exactDailyValue
    ),
    monounsaturatedFats: roundingCheck(nutritions.monounsaturatedFatPerServing),
    polyunsaturatedFats: roundingCheck(nutritions.polyunsaturatedFatPerServing),
    omega3: roundingCheck(nutritions.omega3PerServing),
    omega6: roundingCheck(nutritions.omega6PerServing),
    caffeine: roundingCheck(nutritions.caffeinePerServing),
    ash: roundingCheck(nutritions.ashPerServing),
    water: roundingCheck(nutritions.waterPerServing),
    moisture: roundingCheck(nutritions.moisturePerServing),
    servingQuantityInUnitOfMeasurement: nutritions.quantityPerServingInUnitOfMeasurement.item1,
    servingUnitOfMeasurement: nutritions.quantityPerServingInUnitOfMeasurement.item2,
    volume: nutritions.volume,
    weight: nutritions.weight,
    labelType: nutritions.labelType,
    additionalLabelInformation: nutritions.additionalLabelInformation
  };
}

export function mapNutritionsForDish(nutritions: any): Partial<Nutritions> {
  return {
    calories: nutritions.energyPerServing.roundedValue,
    fat: nutritions.fatPerServing.roundedValue,
    fatDv: nutritions.fatPerServing.roundedDailyValue,
    saturatedFat: nutritions.saturatedFatPerServing.roundedValue,
    transFat: nutritions.transFatPerServing.roundedValue,
    saturatedAndTransDv: nutritions.saturatedAndTransFatPerServing.roundedDailyValue,
    cholesterol: nutritions.cholesterolPerServing.roundedValue,
    sodium: nutritions.sodiumPerServing.roundedValue,
    sodiumDv: nutritions.sodiumPerServing.roundedDailyValue,
    carbohydrate: nutritions.carbohydratePerServing.roundedValue,
    fibre: nutritions.fibrePerServing.roundedValue,
    fibreDv: nutritions.fibrePerServing.roundedDailyValue,
    sugars: nutritions.sugarPerServing.roundedValue,
    sugarsDv: nutritions.sugarPerServing.roundedDailyValue,
    protein: nutritions.proteinPerServing.roundedValue,
    vitaminD: nutritions.vitaminDPerServing.roundedValue,
    vitaminDDv: nutritions.vitaminDPerServing.roundedDailyValue,
    vitaminC: nutritions.vitaminCPerServing.roundedValue,
    potassium: nutritions.potassiumPerServing.roundedValue,
    potassiumDv: nutritions.potassiumPerServing.roundedDailyValue,
    calcium: nutritions.calciumPerServing.roundedValue,
    calciumDv: nutritions.calciumPerServing.roundedDailyValue,
    iron: nutritions.ironPerServing.roundedValue,
    ironDv: nutritions.ironPerServing.roundedDailyValue,
    monounsaturatedFats: nutritions.monounsaturatedFatPerServing.roundedValue,
    polyunsaturatedFats: nutritions.polyunsaturatedFatPerServing.roundedValue,
    omega3: nutritions.omega3PerServing.roundedValue,
    omega6: nutritions.omega6PerServing.roundedValue,
    caffeine: nutritions.caffeinePerServing.roundedValue,
    ash: nutritions.ashPerServing.roundedValue,
    water: nutritions.waterPerServing.roundedValue,
    moisture: nutritions.moisturePerServing.roundedValue,
    servingWeight: nutritions.weightPerServing
  };
}

export function groupBy<K, V>(items: K[], key: (item: K) => V): Map<V, K[]> {
  return items.reduce((acc, item) => {
    const group = key(item);
    const values = acc.get(group) || [];
    values.push(item);
    acc.set(group, values);
    return acc;
  }, new Map<V, K[]>());
}